import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Stack,
    Typography,
    Chip,
    Alert,
    Divider,
} from '@mui/material';
import { Iconify } from 'src/components';
import { NormalLoading } from 'src/components/loading';
import { callApi } from 'src/utils/apiCall';

// Superadmin-only, READ-ONLY diagnostic preview.
// Calls GET /api/v1/admin/task-templates/diagnose/lead/<lead_id>/
// (apps/tasks/diagnostic_views.py::AdminLeadDiagnosticView) — an existing,
// audited, read-only endpoint. This component never issues a write/mutation
// request; there is no confirm/apply action here by design (that is a
// separate, RCA-gated task).

const VERDICT_COLOR = {
    OK: 'success',
    DUPLICATE: 'warning',
    WRONG_TREATMENT: 'error',
    WRONG_STATUS: 'error',
};

export default function BackfillPreviewModal({ open, onClose, leadId }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);

    const fetchDiagnosis = useCallback(async () => {
        if (!leadId) return;
        setLoading(true);
        setError(null);
        const res = await callApi(`/api/v1/admin/task-templates/diagnose/lead/${leadId}/`, 'GET');
        setLoading(false);
        if (!res?.ok) {
            setError(res?.data?.detail || res?.error || 'Failed to load backfill diagnostic.');
            return;
        }
        setResult(res.data);
    }, [leadId]);

    useEffect(() => {
        if (open) {
            fetchDiagnosis();
        } else {
            // Reset on close so a stale result doesn't flash for the next lead.
            setResult(null);
            setError(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, leadId]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth data-testid='backfill-preview-modal'>
            <DialogTitle>
                Backfill preview {leadId ? `— lead #${leadId}` : ''}
            </DialogTitle>
            <DialogContent>
                <Alert severity='info' sx={{ mb: 2 }} icon={<Iconify icon='mdi:eye-outline' />}>
                    Preview only — apply is not yet available.
                </Alert>
                {loading && (
                    <Box sx={{ py: 2 }}>
                        <NormalLoading type='LeadDetails' tableData='No Found' pageCount='No Found' />
                        <Typography sx={{ mt: 1, fontSize: '13px', color: 'text.secondary' }}>
                            Loading diagnostic…
                        </Typography>
                    </Box>
                )}
                {!loading && error && (
                    <Alert severity='error' data-testid='backfill-preview-error'>
                        {error}
                    </Alert>
                )}
                {!loading && !error && result && (
                    <Box data-testid='backfill-preview-result'>
                        <Stack direction='row' spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
                            <Chip size='small' label={`Total: ${result.summary?.total ?? 0}`} />
                            <Chip size='small' color='success' label={`OK: ${result.summary?.ok ?? 0}`} />
                            <Chip size='small' color='warning' label={`Duplicate: ${result.summary?.duplicate ?? 0}`} />
                            <Chip size='small' color='error' label={`Wrong treatment: ${result.summary?.wrong_treatment ?? 0}`} />
                            <Chip size='small' color='error' label={`Wrong status: ${result.summary?.wrong_status ?? 0}`} />
                        </Stack>
                        <Divider sx={{ mb: 2 }} />
                        {(result.checklists || []).length === 0 && (
                            <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                                No checklists found on this lead.
                            </Typography>
                        )}
                        <Stack spacing={1.5}>
                            {(result.checklists || []).map((c) => (
                                <Box
                                    key={c.instance_id}
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: '8px',
                                        p: 1.5,
                                    }}
                                >
                                    <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 0.5 }}>
                                        <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                                            {c.template_name}
                                        </Typography>
                                        <Chip
                                            size='small'
                                            color={VERDICT_COLOR[c.verdict] || 'default'}
                                            label={c.verdict}
                                        />
                                    </Stack>
                                    <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                                        {c.reason}
                                    </Typography>
                                    <Typography sx={{ fontSize: '12px', color: 'text.secondary', mt: 0.5 }}>
                                        Treatment: {c.treatment_name || '—'} · Status: {c.lead_treatment_status || '—'} · Tasks: {c.task_count}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color='inherit'>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}

BackfillPreviewModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

BackfillPreviewModal.defaultProps = {
    leadId: null,
};
